import Box from '@mui/material/Box';
import FormInputLabel from 'common/FormInputLabel';
import FormSelectInput from 'common/FormSelectInput';
import { generateTimeOptions } from 'utils/time';

import { useEffect } from 'react';
import { useState } from 'react';
import { useRef } from 'react';

export default function Availability({
    count,
    handleChange,
    extraAvailabilityWeekDay = [],
    extraAvailabilityStartTime = [],
    extraAvailabilityEndTime = [],
    extraAvailabilityLocation = [],
    extraAvailabilityTelemedecine = [],
    hasTelemedicine,
    formData,
    location,
    loading,
    onlyServicesSelected = false,
    onRemoveAvailability,
    onAddLocationClick,
}) {
    const [weekDays, setWeekDays] = useState([
        {
            id: 1,
            day: 'Monday',
        },
        {
            id: 2,
            day: 'Tuesday',
        },
        {
            id: 3,
            day: 'Wednesday',
        },
        {
            id: 4,
            day: 'Thursday',
        },
        {
            id: 5,
            day: 'Friday',
        },
        {
            id: 6,
            day: 'Saturday',
        },
        {
            id: 7,
            day: 'Sunday',
        },
    ]);

    const [timeSlots, setTimeSlots] = useState(() => generateTimeOptions());
    const [forceInPersonSlots, setForceInPersonSlots] = useState(false);
    const initializedRef = useRef(false);

    // Safely get values from arrays
    const getWeekDayValue = () => {
        return Array.isArray(extraAvailabilityWeekDay) && extraAvailabilityWeekDay.length > count 
            ? extraAvailabilityWeekDay[count] 
            : '';
    };
    
    const getStartTimeValue = () => {
        return Array.isArray(extraAvailabilityStartTime) && extraAvailabilityStartTime.length > count 
            ? extraAvailabilityStartTime[count] 
            : '';
    };
    
    const getEndTimeValue = () => {
        return Array.isArray(extraAvailabilityEndTime) && extraAvailabilityEndTime.length > count 
            ? extraAvailabilityEndTime[count] 
            : '';
    };
    
    const getLocationValue = () => {
        return Array.isArray(extraAvailabilityLocation) && extraAvailabilityLocation.length > count 
            ? extraAvailabilityLocation[count] 
            : '';
    };
    
    const getTelemedecineValue = () => {
        return Array.isArray(extraAvailabilityTelemedecine) && extraAvailabilityTelemedecine.length > count 
            ? extraAvailabilityTelemedecine[count] 
            : false;
    };

    // Derive visit mode dropdown value from flags only (do not override by location)
    const getVisitModeValue = () => {
        const isTelemedicine = getTelemedecineValue();
        if (!isTelemedicine) return 'in_person';
        return forceInPersonSlots ? 'both' : 'telemedicine';
    };

    // Snap a HH:mm value to the closest valid slot at or before it; fallback to first
    const snapToClosestSlot = (value, slots) => {
        if (!value || !Array.isArray(slots) || slots.length === 0) return '';
        const toMinutes = (hhmm) => {
            const [h, m] = String(hhmm).split(':').map(Number);
            return (h || 0) * 60 + (m || 0);
        };
        const target = toMinutes(value);
        let best = slots[0].value;
        let bestMin = toMinutes(best);
        for (const s of slots) {
            const sm = toMinutes(s.value);
            if (sm <= target && sm >= bestMin) {
                best = s.value;
                bestMin = sm;
            }
        }
        return best;
    };

    // Handle visit mode changes by mapping to boolean and aligning times
    const handleVisitModeChange = (e) => {
        const { value } = e.target; // 'both' | 'in_person' | 'telemedicine'
        const willBeTelemedicine = value !== 'in_person';

        // Force in-person slots when 'both' is selected
        setForceInPersonSlots(value === 'both');

        // Capture previous times
        const prevStart = getStartTimeValue();
        const prevEnd = getEndTimeValue();

        // Compute new slot set based on target mode
        const useInPersonGrid = value === 'both' || value === 'in_person';
        const duration = Number(useInPersonGrid ? formData?.in_person_consultation_duration : formData?.telemedicine_consultation_duration) || 15;
        const newSlots = generateTimeOptions(duration);

        // Align start/end to new slot grid, keeping the same wall times when possible
        let newStart = snapToClosestSlot(prevStart, newSlots);
        let newEnd = snapToClosestSlot(prevEnd, newSlots);

        // Ensure end is after start; if not, pick the next available slot after start
        const idxStart = newSlots.findIndex(s => s.value === newStart);
        const idxEnd = newSlots.findIndex(s => s.value === newEnd);
        if (idxEnd <= idxStart) {
            newEnd = newSlots[Math.min(idxStart + 1, newSlots.length - 1)].value;
        }

        // Update times first
        handleChange({ target: { name: 'start_time', value: newStart } });
        handleChange({ target: { name: 'end_time', value: newEnd } });

        // Then update telemedicine flag
        handleChange({ target: { name: 'telemedicine', value: willBeTelemedicine ? '1' : '', checked: willBeTelemedicine } });
    };

    useEffect(() => {
        const isTelemedecine = getTelemedecineValue();
        
        if (!forceInPersonSlots && isTelemedecine && hasTelemedicine) {
            const telemedicineDuration = Number(formData?.telemedicine_consultation_duration) || 15; 
            const telemdecineSlots = generateTimeOptions(telemedicineDuration);
            setTimeSlots(telemdecineSlots);
        } else {
            const inPersonDuration = Number(formData?.in_person_consultation_duration) || 15;
            const inPersonSlots = generateTimeOptions(inPersonDuration);
            setTimeSlots(inPersonSlots);
        }
    }, [formData, extraAvailabilityTelemedecine, count, forceInPersonSlots, hasTelemedicine]);

    // Infer and restore 'both' vs 'telemedicine' on initial load based on saved data
    useEffect(() => {
        if (initializedRef.current) return;
        const isTele = getTelemedecineValue();
        const loc = getLocationValue();
        if (!isTele) {
            initializedRef.current = true;
            return;
        }
        // If this row has telemedicine enabled AND a real in-person location,
        // treat it as "Available In‑Person & Telemedicine" by default. This is the
        // merged representation of two DB rows (one telemedicine, one in‑person).
        if (loc && String(loc) !== '0') {
            setForceInPersonSlots(true);
        } else {
            // Pure telemedicine (location 0 or empty) should remain "Telemedicine only"
            setForceInPersonSlots(false);
        }
        initializedRef.current = true;
    }, []);

    // Ensure selected start/end are valid options for the current slot grid
    useEffect(() => {
        const s = getStartTimeValue();
        const e = getEndTimeValue();
        const hasStart = timeSlots.some(ts => ts.value === s);
        const hasEnd = timeSlots.some(ts => ts.value === e);
        if ((s && !hasStart) || (e && !hasEnd)) {
            const snappedStart = s ? snapToClosestSlot(s, timeSlots) : '';
            const snappedEnd = e ? snapToClosestSlot(e, timeSlots) : '';
            if (snappedStart) handleChange({ target: { name: 'start_time', value: snappedStart } });
            if (snappedEnd) handleChange({ target: { name: 'end_time', value: snappedEnd } });
        }
    }, [timeSlots]);

    return (
        <Box style={{ 
            border: '1px solid #E1E1E1', 
            borderRadius: '8px', 
            padding: '16px',
            paddingLeft: '16px',
            paddingRight: '16px',
            marginBottom: '16px',
            marginLeft: '18px',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            <FormInputLabel label={`Availability ${count + 1}`} labelSize={12} labelBottomMargin={'-mb-1'} style={{ marginBottom: '12px' }} />
            {loading ? (
                <div className={'flex items-center justify-between gap-7 w-full mt-1'} style={{ marginBottom: '0px' }}>
                    <span className="text-lg font-normal animate-pulse text-gray-500">...Loading</span>
                </div>
            ) : (
                <>
                    <div className={'flex items-center justify-between gap-7 w-full mt-1'} style={{ marginBottom: '0px' }}>
                        <FormSelectInput
                            name="location"
                            handleChange={handleChange}
                            value={getLocationValue()}
                            required={false}
                            inputSize={6}
                            placeholder={'Select Location'}
                            options={location}
                            optionKey="id"
                            optionValue={['location']}
                            noLabel={true}
                            style={{ marginBottom: '0px' }}
                            showAddOption={true}
                            addOptionText="+ Add a Location"
                            onAddOptionClick={onAddLocationClick}
                        />
                        <FormSelectInput
                            name="week_day"
                            handleChange={handleChange}
                            value={getWeekDayValue()}
                            required={false}
                            inputSize={6}
                            placeholder={'Select Week Days'}
                            options={weekDays}
                            optionKey="id"
                            optionValue={['day']}
                            noLabel={true}
                            style={{ marginBottom: '0px' }}
                        />
                    </div>
                    <div className={'flex items-center justify-between gap-7 w-full'} style={{ marginBottom: '0px', marginTop: '16px' }}>
                        <FormSelectInput
                            name="start_time"
                            handleChange={handleChange}
                            value={getStartTimeValue()}
                            required={false}
                            inputSize={6}
                            placeholder={'Select Start Time'}
                            options={onlyServicesSelected ? [] : timeSlots}
                            optionKey="value"
                            optionValue={['label']}
                            noLabel={true}
                        />
                        <FormSelectInput
                            name="end_time"
                            handleChange={handleChange}
                            value={getEndTimeValue()}
                            required={false}
                            inputSize={6}
                            placeholder={'Select End Time'}
                            options={onlyServicesSelected ? [] : timeSlots}
                            optionKey="value"
                            optionValue={['label']}
                            noLabel={true}
                        />
                    </div>

                    {hasTelemedicine && (
                        <div className={'w-full mt-2'}>
                            <div className={'flex items-center justify-start space-x-4'}>
                                <FormSelectInput
                                    name="visit_mode"
                                    handleChange={handleVisitModeChange}
                                    value={getVisitModeValue()}
                                    required={false}
                                    inputSize={12}
                                    placeholder={'Select visit mode'}
                                    options={[
                                        { id: 'both', day: 'Available In-Person & Telemedicine' },
                                        { id: 'in_person', day: 'In-Person Only' },
                                        { id: 'telemedicine', day: 'Telemedicine Only' },
                                    ]}
                                    optionKey="id"
                                    optionValue={['day']}
                                    className="long-label"
                                    noLabel={true}
                                />
                            </div>
                        </div>
                    )}
                    <div className={'flex items-center justify-between mt-3'}>
                        {onRemoveAvailability && (
                            <Box
                                className={'danger-link cursor-pointer'}
                                onClick={onRemoveAvailability}
                            >
                                - Remove Availability
                            </Box>
                        )}
                    </div>
                </>
            )}
        </Box>
    );
}
